import React from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import type {UserGroupModulePermissionResponse} from 'src/api/ProactApiClient';

import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetModulePermissionSchemaQuery} from 'src/redux/queries/userGroupPermissions';

import {Link} from 'react-router-dom';
import {
  GetModulePermissionFieldLabel,
  MatchModulePermissionSchemaToPermissions,
} from 'src/app/user-groups/userGroupHelpers';

export interface EffectiveModulePermissionsProps {
  permissions?: UserGroupModulePermissionResponse[];
}
export const EffectiveModulePermissions: React.FC<
  EffectiveModulePermissionsProps
> = ({permissions}) => {
  const {data: permissionSchema, isLoading: isSchemaLoading} =
    useGetModulePermissionSchemaQuery();

  const nodes =
    permissions &&
    permissionSchema &&
    permissionSchema?.modules
      .map(module => {
        const nodes: React.ReactNode[] = [];

        nodes.push(
          <Title size="sm" mt="lg">
            {module.name}
          </Title>
        );

        module.operations.forEach(operation => {
          const permission = MatchModulePermissionSchemaToPermissions(
            module.module,
            operation,
            permissions
          );

          if (permission) {
            nodes.push(
              <Group mt="xs">
                <Text size="sm">
                  {GetModulePermissionFieldLabel(operation)}
                </Text>
                {permission?.userGroup?.id && (
                  <Box>
                    <Anchor
                      component={Link}
                      to={PathUtility.UserGroupView(
                        permission.userGroup.id,
                        'module-permissions'
                      )}
                      target="_blank"
                      size="xs"
                      c="dimmed">
                      → {permission.userGroup.name}
                    </Anchor>
                  </Box>
                )}
              </Group>
            );
          }
        });

        // If we don't have any rows, we show nothing for the module.
        if (nodes.length === 1) {
          return [];
        }

        return nodes;
      })
      .reduce((acc, current) => acc.concat(current), []);

  return isSchemaLoading ? (
    <TableBodyRender isLoading={isSchemaLoading} />
  ) : (
    <>
      {!permissions && (
        <Text>
          No permissions found for this user. Are they a member of a user group?
        </Text>
      )}
      {nodes && nodes.length > 1 ? (
        <>{nodes}</>
      ) : (
        <Text size="xs" c="dimmed">
          No permissions found.
        </Text>
      )}
    </>
  );
};
